import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuditLog } from '@/lib/audit-api'
import { cn } from '@/lib/utils'

const AUDIT_ENTITY_TYPES = ['', 'Invoice', 'Client', 'Project', 'Category', 'RecurringInvoiceTemplate', 'TimeEntry', 'TravelExpense', 'WeekApproval', 'WorkdayException', 'ClientRate', 'PersonRate', 'Opportunity', 'PersonContractHours', 'DistributionScript', 'Person', 'ExternalParty', 'InvoiceTemplate', 'User', 'OAuthClient', 'OAuthGrant', 'AppSetting', 'HoursReport', 'Notification', 'Selftest', 'Auth']
const CHANNEL_LABEL: Record<string, string> = { WEB: 'Web', MCP: 'MCP', SCHEDULER: 'Scheduler', IMPORT: 'Import', MANUAL: 'Handmatig' }

export function AuditTab({ active }: { active: boolean }) {
  const [entityType, setEntityType] = useState('')
  const [channel, setChannel] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<string | null>(null)

  const filters = { entityType: entityType || undefined, channel: channel || undefined, from: from || undefined, to: to || undefined }
  const query = useAuditLog(filters, page, active)
  const items = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / 50))

  function formatDiff(diff: Record<string, unknown> | null): string {
    if (!diff) return ''
    return Object.entries(diff)
      .map(([key, value]) => {
        if (value !== null && typeof value === 'object' && 'from' in (value as object) && 'to' in (value as object)) {
          const v = value as { from: unknown; to: unknown }
          return `${key}: ${JSON.stringify(v.from)} -> ${JSON.stringify(v.to)}`
        }
        return `${key}: ${JSON.stringify(value)}`
      })
      .join(' · ')
  }

  return (
    <Card>
      <CardHeader title="Audit trail" subtitle={`Append-only logboek van alle wijzigingen (${total} regels)`} />

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <span id="audit-entity-label" className="mb-1.5 block text-xs font-medium text-ink-faint">Entiteit</span>
          <Select
            value={entityType === '' ? 'all' : entityType}
            onValueChange={(v) => { setEntityType(v === 'all' ? '' : v); setPage(1) }}
            options={AUDIT_ENTITY_TYPES.map((t) => ({ value: t === '' ? 'all' : t, label: t === '' ? 'Alle' : t === 'Auth' ? 'Logins' : t }))}
            className="w-52"
            ariaLabelledby="audit-entity-label"
          />
        </div>
        <div>
          <span id="audit-channel-label" className="mb-1.5 block text-xs font-medium text-ink-faint">Kanaal</span>
          <Select
            value={channel === '' ? 'all' : channel}
            onValueChange={(v) => { setChannel(v === 'all' ? '' : v); setPage(1) }}
            options={[{ value: 'all', label: 'Alle' }, ...Object.entries(CHANNEL_LABEL).map(([value, label]) => ({ value, label }))]}
            className="w-36"
            ariaLabelledby="audit-channel-label"
          />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-faint">Vanaf</span>
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1) }} className="field h-9 py-0" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-ink-faint">Tot en met</span>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1) }} className="field h-9 py-0" />
        </label>
      </div>

      {query.isError ? (
        <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
      ) : query.isLoading ? (
        <Skeleton className="mt-4 h-48 w-full" />
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-ink-faint">Geen audit-regels voor deze filters.</p>
      ) : (
        <div className="mt-4 divide-y divide-hairline">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setExpanded(expanded === item.id ? null : item.id)}
              className="block w-full py-2.5 text-left"
            >
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="num shrink-0 text-xs text-ink-faint">{new Date(item.createdAt).toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <span className="text-sm font-medium text-ink">{item.entityType === 'Auth' ? 'Login' : item.entityType}</span>
                <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-soft">{item.action}</span>
                <span className="text-sm text-ink-soft">{item.actorName ?? 'systeem'}</span>
                <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-faint">{CHANNEL_LABEL[item.channel] ?? item.channel}</span>
              </div>
              <p className={cn('mt-1 text-xs text-ink-faint', expanded === item.id ? 'whitespace-pre-wrap' : 'truncate')}>
                {formatDiff(item.diff)}
              </p>
            </button>
          ))}
        </div>
      )}

      {pages > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <button type="button" className="btn-ghost" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            Vorige
          </button>
          <span className="text-xs text-ink-faint">Pagina {page} van {pages}</span>
          <button type="button" className="btn-ghost" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}>
            Volgende
          </button>
        </div>
      ) : null}
    </Card>
  )
}
